// 상태 바: 프로젝트 루트, 백엔드 모드, 스크립트 언어, 저장 상태, 엔진 상태(실행기), 언어 서버, 테마.
// 알릴 것이 없는 항목(열린 프로젝트가 없을 때의 언어, 대기 중이거나 꺼진 언어 서버)은 보이지 않는다.

import { observer } from "mobx-react-lite";
import { MODE_LABELS } from "../editor/backends";
import { useEditor } from "../editor/EditorContext";
import type { ScriptLanguageServer } from "../editor/scripting/lsp/LanguageServer";
import "./StatusBar.css";

const THEME_LABEL = { dark: "다크", light: "라이트" } as const;

/** 엔진: 없음 / 대기 / 실행 중 PID 1234 00:42 / 종료 코드 1 (문구는 RunnerStore.statusText) */
const EngineStatus = observer(function EngineStatus() {
  const runner = useEditor().runner;
  const crashed = runner.state === "idle" && runner.exitCode !== null && runner.exitCode !== 0;
  const className = ["statusbar-item", "statusbar-engine", runner.isRunning ? "running" : "", crashed ? "failed" : ""].filter(Boolean).join(" ");
  return (
    <span className={className} data-testid="status-engine" title={runner.statusTitle}>
      {runner.statusText}
    </span>
  );
});

/** 언어 서버 하나: 시작 중, LuaLS 3.19.1, Ruby 분석기, 오류. 서버를 띄우는 방법이 없거나 대기 중이거나 꺼졌으면 보이지 않는다 */
const LanguageServerStatus = observer(function LanguageServerStatus({ server }: { server: ScriptLanguageServer }) {
  if (!server.hasLauncher || server.state === "idle" || server.state === "off") return null;
  const className = ["statusbar-item", "statusbar-lsp", server.state === "running" ? "running" : "", server.state === "failed" ? "failed" : ""].filter(Boolean).join(" ");
  return (
    <span className={className} data-testid={`status-lsp-${server.languageId}`} data-state={server.state} title={server.statusTitle}>
      {server.statusText}
    </span>
  );
});

export const StatusBar = observer(function StatusBar() {
  const editor = useEditor();
  const project = editor.project;
  const dirty = editor.documents.dirtyDocuments.length;
  const themePref = editor.settings.settings.theme;
  const theme = THEME_LABEL[editor.theme.applied] + (themePref === "system" ? " (시스템)" : "");
  return (
    <div className="statusbar" data-testid="statusbar">
      <span className="statusbar-item statusbar-root" title={project.root || undefined}>
        {project.isOpen ? project.root : "열린 프로젝트 없음"}
      </span>
      <span className="statusbar-item">{MODE_LABELS[editor.mode]}{editor.bridgeUrl ? ` ${editor.bridgeUrl}` : ""}</span>
      {project.isOpen && <span className="statusbar-item">{project.gameJson.script === "lua" ? "Lua" : "Ruby"}</span>}
      <span className="statusbar-item">{dirty > 0 ? `저장 안 된 문서 ${dirty}개` : "저장됨"}</span>
      <EngineStatus />
      {editor.scripting.languageServers.map((server) => (
        <LanguageServerStatus key={server.languageId} server={server} />
      ))}
      <span className="statusbar-spacer" />
      <span className="statusbar-item" data-testid="status-theme">
        {theme}
      </span>
    </div>
  );
});
